import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowRight, Phone, Shield, ShieldCheck, Clock, Wrench, Truck, Flame, Grid3X3, Palette, Lightbulb, Star, Thermometer, ChevronLeft, ChevronRight } from 'lucide-react';

const trustSignals = [
  { icon: Shield, title: '90-Day Warranty', desc: 'Parts & labour covered' },
  { icon: Clock, title: '4-7 Day Turnaround', desc: 'Fast, reliable repairs' },
  { icon: Wrench, title: 'Genuine Parts', desc: 'Manufacturer components' },
  { icon: Truck, title: 'Free Collection', desc: 'Across all London' },
];

const brands = ['Epson', 'Optoma', 'BenQ', 'Sony', 'Panasonic', 'ViewSonic', 'NEC', 'JVC', 'Acer', 'Hisense', 'Samsung', 'LG', 'Barco', 'Christie', 'Sanyo', 'Hitachi', 'Vivitek', 'DHN', 'Xiaomi', 'Wemax'];

const services = [
  { icon: Grid3X3, title: 'Projector White Dots Repair', desc: 'Starry night effect on your DLP projector? Dead pixels spreading across the image? Our engineers replace the faulty DMD chip with a genuine component and realign the optical block. 90-day warranty.', href: '/services/white-dots-dmd', tag: 'DLP CHIP', image: '/images/dmd-chip.jpg' },
  { icon: Thermometer, title: 'Projector Power Repair & Overheating Fix', desc: 'Projector shutting down after 5-10 minutes? Temperature light flashing? We perform a full thermal overhaul including ultrasonic filter cleaning, fan replacement, and power supply diagnostics.', href: '/services/overheating-power', tag: 'THERMAL', image: '/images/power-issue.png' },
  { icon: Palette, title: 'Projector Display Problem & Discolouration', desc: 'Yellow, brown or purple stains on the image? Flickering colours? Black and white only? We replace burnt LCD polarizers and faulty colour wheels to restore full colour accuracy.', href: '/services/display-discolouration', tag: 'DISPLAY', image: '/images/colour-wheel.png' },
  { icon: Lightbulb, title: 'Projector Lamp Replacement', desc: 'Lamp not striking? Projector turning on then immediately off? Dim image even with a new lamp? We supply and fit genuine OEM lamps for all brands. Every replacement includes a free optical cleaning service.', href: '/services/laser-lamp', tag: 'LAMP', image: '/images/laser-light-source.jpg' },
];

const comparisonData = [
  { feature: 'Diagnostic Assessment', us: 'Free — within 24 hours', typical: '£40-£80 + waiting time' },
  { feature: 'Collection & Delivery', us: 'Free — same-day London, UK-wide', typical: '£15-£30 each way or drop-off only' },
  { feature: 'Turnaround Time', us: '4-7 business days', typical: '1-3 weeks' },
  { feature: 'Parts Used', us: 'Genuine OEM only', typical: 'Compatible or aftermarket' },
  { feature: 'Warranty', us: '90 days parts & labour', typical: '30 days or none' },
  { feature: 'Micro-Soldering', us: 'In-house board-level repair', typical: 'Board replacement only' },
  { feature: 'Onsite Service', us: 'Available for commercial installs', typical: 'Workshop only' },
];

const faults = [
  { icon: Flame, title: 'Projector Overheating & Thermal Shutdown', desc: 'Fans running loud or unit shutting down after 10-30 minutes? Grinding noise from the fan? These are signs of blocked airways, failed cooling fans, or dried thermal paste. We deep-clean the entire cooling system and replace faulty components.', href: '/services/overheating-power', image: '/images/overheating-repair.jpg' },
  { icon: Grid3X3, title: 'Projector White Dots on Screen', desc: 'Starry night effect with white or dead pixels appearing on your DLP projector? This is a classic DMD chip failure — extremely common on Optoma, BenQ and Acer models. We replace the DMD chip with a precision-aligned genuine component.', href: '/services/white-dots-dmd', image: '/images/white-dots.jpg' },
  { icon: Palette, title: 'Projector Colour Wheel Repair', desc: 'Flickering rainbow colours? Image showing only in black and white? Unusual grinding noise on startup? These are clear signs of a damaged colour wheel in a DLP projector. We replace with a precision-balanced genuine component and recalibrate.', href: '/services/display-discolouration', image: '/images/display-discolouration.jpg' },
];

const whyChoose = [
  { num: '01', title: 'Qualified Engineers', desc: 'Our repair technicians are trained and certified in projector electronics, optical systems, and micro-soldering. We diagnose at component level — not board replacement.' },
  { num: '02', title: 'Free Inspection & Quote', desc: 'Submit your enquiry for a free diagnostic assessment. We provide a detailed repair quote within 24 hours. No obligation, no hidden charges.' },
  { num: '03', title: 'Genuine OEM Parts Only', desc: 'We source manufacturer-original parts from authorised distributors. No aftermarket substitutes. Every part is traceable and warrantied.' },
  { num: '04', title: '90-Day Warranty on All Repairs', desc: 'Full parts and labour coverage for 90 days. If the same fault reoccurs, we fix it free of charge. Priority support during warranty period.' },
  { num: '05', title: 'Free Collection UK-Wide', desc: 'Same-day collection across London. Nationwide pickup and return service for all UK postcodes. Ireland service available through our Dublin centre.' },
  { num: '06', title: 'Micro-Soldering Specialists', desc: 'Board-level repairs that other centres cannot perform. We repair mainboards, power supplies, and HDMI boards at component level — saving you hundreds.' },
];

const processSteps = [
  { step: '1', title: 'Book', desc: 'Submit repair request online or call us' },
  { step: '2', title: 'Collect', desc: 'Free same-day collection from your location' },
  { step: '3', title: 'Diagnose', desc: 'Full diagnostic assessment within 24 hours' },
  { step: '4', title: 'Repair', desc: 'Expert repair with genuine parts' },
  { step: '5', title: 'Return', desc: 'Delivered back with 90-day warranty' },
];

const testimonials = [
  { text: "Like a new projector! Our Epson was showing yellow stains. They replaced the polarizers and it's crystal clear now. Amazing service.", author: 'Sarah J.', role: 'Corporate AV Manager, London', stars: 5 },
  { text: "Our Optoma had the dreaded white dots. Thought it was a write-off. They replaced the DMD chip in 3 days and it's perfect again. Saved me buying a new projector.", author: 'Michael T.', role: 'Home Cinema Owner, Surrey', stars: 5 },
  { text: "Our school has 12 classroom projectors. They collected during half term, serviced every unit, replaced worn fans and filters. All running like new. Highly recommended.", author: 'David L.', role: 'School IT Head, Birmingham', stars: 5 },
  { text: "Projector kept shutting down after 15 minutes — temperature warning. They found the fan had seized and the thermal paste was dry. Full thermal overhaul and now runs perfectly silent.", author: 'Emma W.', role: 'Business Owner, Manchester', stars: 5 },
  { text: "No image on our Sony projector after a power surge. Other repair centres said replace the mainboard. These guys micro-soldered the damaged components and saved us £400.", author: 'James R.', role: 'Conference Centre Manager, Leeds', stars: 5 },
  { text: "Had a JVC with flickering colours. Turned out to be the colour wheel. They sourced the part and fitted it within a week. Professional from start to finish.", author: 'Amanda K.', role: 'Museum Curator, Bristol', stars: 5 },
];

const maintenanceTips = [
  { title: 'How to Extend Your Projector Lamp Life', desc: 'Allow proper cool-down before powering off, clean filters monthly, and avoid switching the projector on and off rapidly. A well-maintained lamp can last 2,000-5,000 hours.', href: '#' },
  { title: 'Why Your Projector Keeps Overheating', desc: 'Blocked air filters are the #1 cause of overheating. Clean or replace filters every 3-6 months depending on usage. Signs include loud fans, temperature warnings, and automatic shutdown.', href: '#' },
  { title: 'Projector White Dots: What Causes Them', desc: 'White dots on DLP projectors are caused by failed mirrors on the DMD chip. This is a mechanical failure that worsens over time. Early repair prevents the chip from failing completely.', href: '#' },
  { title: 'Annual Projector Servicing: Is It Worth It?', desc: 'Yes. Annual servicing can extend your projector lifespan by 30-50%. We clean the optical path, replace filters, check the cooling system, and update firmware. Preventative maintenance costs far less than major repairs.', href: '#' },
];

const heroSlides = [
  { src: '/images/slide-1.jpg', alt: 'Epson L890E Projector Repair' },
  { src: '/images/slide-2.jpg', alt: 'Epson QL7000 4K HDR 3LCD Laser Projector' },
  { src: '/images/slide-3.jpg', alt: 'Optoma UHP Lamp Projector' },
  { src: '/images/slide-4.png', alt: '4K Laser Projector Repair' },
  { src: '/images/slide-5.png', alt: 'Colour Wheel Repair' },
  { src: '/images/slide-6.png', alt: 'Home Cinema Projector' },
  { src: '/images/slide-7.jpg', alt: 'Projector Internal Components Repair' },
];

const errorCodes = [
  { brand: 'Epson', indicator: 'Temp flashing', symptom: 'Unit shuts down after 5-10 minutes.', resolution: 'Thermal Overhaul', detail: 'Filter replacement & fan clearance' },
  { brand: 'Optoma', indicator: 'LED Lamp solid red', symptom: 'Won\'t strike lamp; clicking noise.', resolution: 'Lamp/Ballast Repair', detail: 'OEM lamp & ballast check' },
  { brand: 'BenQ', indicator: 'Fan LED flashing', symptom: 'Loud grinding or immediate shutdown.', resolution: 'Fan Replacement', detail: 'New magnetic levitation fans' },
  { brand: 'Sony', indicator: '3 Flashes (Standby)', symptom: 'Lamp cover sensor or mainboard fault.', resolution: 'Sensor/Board Diagnostic', detail: 'Micro-switch & board reflow' },
  { brand: 'Panasonic', indicator: 'Filter Warning', symptom: 'Clogged filter; reduced airflow.', resolution: 'Airway Purge', detail: 'Ultrasonic filter cleaning' },
];

function HeroSection() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % heroSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const goPrev = () => setCurrent(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  const goNext = () => setCurrent(prev => (prev + 1) % heroSlides.length);

  return (
    <section className="relative flex items-center overflow-hidden bg-[#0057B7]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_#FFDD0015_0%,_transparent_70%)]" />
      <div className="container-custom relative z-10 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* Left: Text */}
          <div>
            <span className="font-label text-[#0057B7] tracking-widest text-sm">LASER &bull; XENON &bull; DLP &bull; LCD</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-[1.05] mt-4">
              London&apos;s Trusted<br />
              <span className="text-[#FFDD00]">Projector Repair</span><br />
              Specialists
            </h1>
            <p className="mt-5 text-lg text-white/70 max-w-xl leading-relaxed">
              Expert diagnostics and precision repairs for DLP, LCD, laser and xenon projectors across the UK. Free collection. 90-day warranty. Qualified engineers. Genuine parts only.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/repair-enquiry" className="btn-primary text-base px-8 py-4">
                Repair Enquiry <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="tel:+443330064326" className="btn-outline text-base px-8 py-4">
                <Phone className="w-5 h-5" /> 0333 006 4326
              </a>
            </div>
          </div>

          {/* Right: Image Slider */}
          <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#003D80] aspect-[4/3]">
            {heroSlides.map((slide, i) => (
              <div
                key={slide.src}
                className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0'}`}
              >
                <img src={slide.src} alt={slide.alt} className="w-full h-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0057B7]/60 via-transparent to-transparent" />
              </div>
            ))}

            {/* Slide counter */}
            <div className="absolute top-4 right-4 px-3 py-1 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium">
              {current + 1} / {heroSlides.length}
            </div>

            {/* Navigation arrows */}
            <button
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors"
              aria-label="Previous slide"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors"
              aria-label="Next slide"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`h-2 rounded-full transition-all ${i === current ? 'w-6 bg-[#FFDD00]' : 'w-2 bg-white/40 hover:bg-white/60'}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  // CSS reveal-up animation used instead of GSAP for reliability

  return (
    <>
      <SEO title="Projector Repairs UK | DLP, LCD, Laser & Xenon | Free Collection" description="Expert projector repairs across the UK. White dots, overheating, discolouration, lamp replacement. Free collection. 90-day warranty. Qualified engineers. Genuine parts." canonical="https://projectorrepairlondon.co.uk" />

      {/* ─── HERO — DARK — 2 COLUMN ─── */}
      <HeroSection />

      {/* ─── LEAD MAGNETS — GOLD ─── */}
      <section className="bg-[#FFDD00] py-5">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/repair-enquiry" className="flex items-center gap-4 bg-[#0057B7]/10 rounded-xl p-5 hover:bg-[#0057B7]/20 transition-colors group">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#0057B7]/20">
                <Wrench className="w-6 h-6 text-[#0057B7]" />
              </div>
              <div>
                <p className="font-heading font-semibold text-[#0057B7]">Book a Free Diagnostic</p>
                <p className="text-sm text-[#0057B7]/70">No-obligation assessment within 24 hours</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#0057B7]/70 ml-auto group-hover:translate-x-1 transition-transform" />
            </Link>
            <a href="tel:+443330064326" className="flex items-center gap-4 bg-[#0057B7]/10 rounded-xl p-5 hover:bg-[#0057B7]/20 transition-colors group">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#0057B7]/20">
                <Phone className="w-6 h-6 text-[#0057B7]" />
              </div>
              <div>
                <p className="font-heading font-semibold text-[#0057B7]">Speak to an Engineer</p>
                <p className="text-sm text-[#0057B7]/70">Call now for immediate assistance</p>
              </div>
              <ArrowRight className="w-5 h-5 text-[#0057B7]/70 ml-auto group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* ─── EXTENDED WARRANTY BANNER — BLUE / YELLOW ─── */}
      <section className="bg-[#0057B7] py-6 md:py-8 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-10 -top-10 w-40 h-40 border-4 border-[#FFDD00] rounded-full" />
          <div className="absolute -left-10 -bottom-10 w-32 h-32 border-4 border-[#FFDD00] rounded-full" />
        </div>
        <div className="container-custom relative z-10">
          <Link to="/extended-warranty" className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 group">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#FFDD00] shrink-0">
                <ShieldCheck className="w-7 h-7 text-[#0057B7]" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 bg-[#FFDD00] text-[#0057B7] text-[10px] font-bold rounded uppercase tracking-wider">Extended Warranty</span>
                  <span className="text-white/40 text-xs">12 Month Protection</span>
                </div>
                <p className="font-heading text-lg md:text-xl font-bold text-white leading-tight">
                  Protect Your Projector for Just <span className="text-[#FFDD00]">45p Per Day</span>
                </p>
                <p className="text-sm text-white/60 mt-1">
                  Covers parts, labour, power supply, overheating, display & mainboard. Free collection. Priority turnaround.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="hidden md:flex flex-col items-end">
                <span className="text-2xl font-bold text-[#FFDD00]">£164.25<span className="text-sm text-white/60 font-normal">/year</span></span>
              </div>
              <div className="flex items-center gap-2 px-5 py-3 bg-[#FFDD00] text-[#0057B7] rounded-lg font-bold group-hover:bg-[#C9A800] transition-colors">
                Learn More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ─── TRUST BAR — WHITE ─── */}
      <section className="bg-white py-8">
        <div className="container-custom">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {trustSignals.map((s) => (
              <div key={s.title} className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#FFDD00]/10 text-[#0057B7]">
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0057B7]">{s.title}</p>
                  <p className="text-xs text-[#0057B7]/70">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SERVICES — WHITE ─── */}
      <section className="bg-white pt-12 lg:pt-16 pb-6 lg:pb-8">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7]">Services</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7] mt-2">Projector Repair Services UK</h2>
            <p className="mt-3 text-[#0057B7]/70 leading-relaxed text-sm">Expert repairs for DLP, LCD, laser and xenon projectors. Free collection across London and the UK. All work covered by our 90-day warranty.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 reveal-up">
            {services.map((s) => (
              <Link key={s.href} to={s.href} className="bg-[#E8F0FE] rounded-xl overflow-hidden border border-[#0057B7]/5 group hover:border-[#FFDD00]/30 transition-all card-hover">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6">
                  <span className="text-[10px] font-label text-[#0057B7] bg-[#FFDD00]/10 px-2 py-1 rounded">{s.tag}</span>
                  <h3 className="font-heading font-semibold text-lg text-[#0057B7] mt-3 mb-2">{s.title}</h3>
                  <p className="text-sm text-[#0057B7]/70 leading-relaxed mb-4">{s.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-[#0057B7]">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMMON FAULTS — CREAM ─── */}
      <section className="bg-[#E8F0FE] pt-6 lg:pt-8 pb-6 lg:pb-8">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7]">Common Faults</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7] mt-2">Common Projector Problems We Fix</h2>
            <p className="mt-3 text-[#0057B7]/70 text-sm">Recognise these symptoms? Click through to learn how our qualified engineers diagnose and resolve each fault with genuine parts and a 90-day warranty.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 reveal-up">
            {faults.map((f) => (
              <Link key={f.href} to={f.href} className="bg-white rounded-xl overflow-hidden border border-[#0057B7]/5 group hover:border-[#FFDD00]/30 transition-all card-hover">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={f.image} alt={f.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#FFDD00]/10 text-[#0057B7] mb-3">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-[#0057B7] mb-2">{f.title}</h3>
                  <p className="text-sm text-[#0057B7]/70 leading-relaxed mb-4">{f.desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-[#0057B7]">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SERVICE COMPARISON — WHITE ─── */}
      <section className="bg-white pt-6 lg:pt-8 pb-6 lg:pb-8">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7]">Compare</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7] mt-2">How We Compare to Other Repair Services</h2>
            <p className="mt-3 text-[#0057B7]/70 text-sm">See why homes, schools and businesses across the UK choose Projector Repair London.</p>
          </div>
          <div className="overflow-x-auto reveal-up">
            <table className="w-full min-w-[600px] bg-[#E8F0FE] rounded-xl overflow-hidden">
              <thead><tr className="bg-[#0057B7]">
                <th className="px-6 py-4 text-left text-xs font-label text-white/60">Feature</th>
                <th className="px-6 py-4 text-left text-xs font-label text-[#0057B7]">Projector Repair London</th>
                <th className="px-6 py-4 text-left text-xs font-label text-white/60">Typical Repair Service</th>
              </tr></thead>
              <tbody className="divide-y divide-white">
                {comparisonData.map((row, i) => (
                  <tr key={i} className="hover:bg-white transition-colors">
                    <td className="px-6 py-4 font-semibold text-[#0057B7] text-sm">{row.feature}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0057B7]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] shrink-0" />{row.us}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#0057B7]/60">{row.typical}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─── WHY CHOOSE — WHITE ─── */}
      <section className="bg-white pt-6 lg:pt-8 pb-6 lg:pb-8">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7]">Trust</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7] mt-2">Why UK Businesses &amp; Homes Choose Us</h2>
            <p className="mt-3 text-[#0057B7]/70 text-sm max-w-2xl mx-auto">Part of Wells Electronics Ltd. Qualified technicians. Free diagnostics. Genuine parts. 90-day warranty. Nationwide service.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 reveal-up">
            {whyChoose.map((w) => (
              <div key={w.num} className="bg-[#E8F0FE] rounded-xl p-6">
                <span className="font-heading text-4xl font-extrabold text-[#0057B7]/20">{w.num}</span>
                <h3 className="font-heading font-semibold text-lg text-[#0057B7] mt-2 mb-2">{w.title}</h3>
                <p className="text-sm text-[#0057B7]/70 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PROCESS STEPS — CREAM ─── */}
      <section className="bg-[#E8F0FE] pt-6 lg:pt-8 pb-6 lg:pb-8">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7]">Process</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7] mt-2">How It Works</h2>
          </div>
          <div className="relative reveal-up">
            <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-[#0057B7]/10" />
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-8">
              {processSteps.map((step) => (
                <div key={step.step} className="text-center relative z-10">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#FFDD00] text-[#0057B7] font-heading font-bold text-xl mx-auto mb-4">{step.step}</div>
                  <h3 className="font-heading font-semibold text-[#0057B7] mb-1">{step.title}</h3>
                  <p className="text-sm text-[#0057B7]/70">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── ERROR CODE TABLE — WHITE ─── */}
      <section className="bg-white pt-6 lg:pt-8 pb-6 lg:pb-8">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7]">Quick Reference</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7] mt-2">Common Projector Error Codes</h2>
          </div>
          <div className="overflow-x-auto reveal-up">
            <table className="w-full min-w-[800px] bg-[#E8F0FE] rounded-xl overflow-hidden">
              <thead><tr className="bg-[#0057B7]">
                <th className="px-6 py-4 text-left text-xs font-label text-white/60">Brand</th>
                <th className="px-6 py-4 text-left text-xs font-label text-white/60">Error</th>
                <th className="px-6 py-4 text-left text-xs font-label text-white/60">Symptom</th>
                <th className="px-6 py-4 text-left text-xs font-label text-white/60">Resolution</th>
              </tr></thead>
              <tbody className="divide-y divide-white">
                {errorCodes.map((row, i) => (
                  <tr key={i} className="hover:bg-white transition-colors">
                    <td className="px-6 py-5 font-semibold text-[#0057B7]">{row.brand}</td>
                    <td className="px-6 py-5"><span className="px-2.5 py-1 bg-red-50 text-red-600 text-xs font-medium rounded-full">{row.indicator}</span></td>
                    <td className="px-6 py-5 text-sm text-[#0057B7]/60">{row.symptom}</td>
                    <td className="px-6 py-5">
                      <span className="text-sm font-medium text-[#0057B7]">{row.resolution}</span>
                      <p className="text-xs text-[#0057B7]/60 mt-0.5">{row.detail}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS — CREAM ─── */}
      <section className="bg-[#E8F0FE] pt-6 lg:pt-8 pb-6 lg:pb-8">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7]">Reviews</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7] mt-2">What Our Customers Say</h2>
            <p className="mt-3 text-[#0057B7]/70 text-sm max-w-2xl mx-auto">Real reviews from real customers across the UK. White dots, overheating, discolouration, lamp failures — we have fixed them all.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 reveal-up">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border-l-4 border-[#FFDD00]">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#0057B7] text-[#0057B7]" />
                  ))}
                </div>
                <p className="text-sm text-[#0057B7]/70 leading-relaxed mb-4">&ldquo;{t.text}&rdquo;</p>
                <p className="text-sm font-semibold text-[#0057B7]">{t.author}</p>
                <p className="text-xs text-[#0057B7]/60">{t.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BRAND MARQUEE — WHITE ─── */}
      <section className="bg-white py-8 overflow-hidden">
        <div className="container-custom text-center mb-5">
          <span className="font-label text-[#0057B7]/30">Authorised repair centre for leading brands</span>
        </div>
        <div className="relative overflow-hidden" style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)' }}>
          <div className="marquee-track">
            {[...brands, ...brands, ...brands, ...brands].map((name, i) => (
              <div key={`${name}-${i}`} className="flex items-center justify-center px-8 py-3 text-[#0057B7]/20 hover:text-[#0057B7]/60 transition-colors select-none">
                <span className="text-lg font-heading font-semibold tracking-wide whitespace-nowrap">{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MAINTENANCE TIPS — CREAM ─── */}
      <section className="bg-[#E8F0FE] py-12 lg:py-16">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="font-label text-[#0057B7]">Knowledge Centre</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7] mt-2">Projector Maintenance Tips</h2>
            <p className="mt-3 text-[#0057B7]/70 text-sm">Practical advice to extend your projector life and avoid common faults. Annual servicing can extend lifespan by 30-50%.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 reveal-up">
            {maintenanceTips.map((tip, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-[#0057B7]/5 hover:border-[#FFDD00]/20 transition-all">
                <h3 className="font-heading font-semibold text-lg text-[#0057B7] mb-2">{tip.title}</h3>
                <p className="text-sm text-[#0057B7]/70 leading-relaxed mb-4">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA — GOLD ─── */}
      <section className="bg-[#FFDD00] py-16">
        <div className="container-custom text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0057B7]">Ready to Fix Your Projector?</h2>
          <p className="mt-4 text-[#0057B7]/70 max-w-lg mx-auto">Book your repair today and get back to crystal-clear projection. Free collection across London and the UK. Repairs for homes, schools, offices and cinemas.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link to="/repair-enquiry" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0057B7] text-[#FFDD00] rounded-lg font-bold hover:bg-[#003D80] transition-all text-lg">Repair Enquiry</Link>
            <a href="tel:+443330064326" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0057B7]/10 text-[#0057B7] rounded-lg font-medium hover:bg-[#0057B7]/20 transition-all text-lg border border-[#0057B7]/20">
              <Phone className="w-5 h-5" /> 0333 006 4326
            </a>
          </div>
        </div>
      </section>
    </>
  );
}